/**
 * TERRAIN SHAPE — where the river and the road are, as one authority.
 *
 * These curves were previously defined inside HomeFieldEnvironment's
 * constructor, which meant every other system that needed to know where the
 * water was had to guess. The prop layout guessed, and guessed wrong: hangars
 * and grazing cattle were authored at x positions the meander sweeps through,
 * so they rendered standing in the James.
 *
 * SEAMLESS WRAP. The terrain repeats every TERRAIN_WRAP units, so every
 * centreline must complete a WHOLE number of cycles across that span —
 * otherwise the two ends do not match and the road visibly jumps when the
 * stage loops. Integer multiples of the fundamental frequency guarantee it by
 * construction.
 */

/**
 * World-units per terrain repeat.
 *
 * WHY 900 AND NOT 220. The terrain used to repeat every 220 units, which at
 * the 12 u/s scroll is **18 seconds**. A run to the boss is around 170 s, so
 * the player crossed the identical river bend, the identical road curve and
 * the identical field pattern roughly NINE times — which is what makes a stage
 * read as "a repeating roll" no matter how good any single frame looks.
 *
 * 900 matches the prop layout's CYCLE exactly, so terrain and dressing now
 * repeat together as one 75-second composition rather than beating against
 * each other at 220 vs 900. The player sees it about twice.
 *
 * The frequency multipliers below are scaled by the same factor so the meander
 * keeps its original wavelength — a longer period must not mean a lazier
 * river, only a river that takes longer to say the same thing twice.
 */
export const TERRAIN_WRAP = 900

const phase = (y: number) => (y * Math.PI * 2) / TERRAIN_WRAP

/**
 * The James. A meander — water is not engineered.
 *
 * Multipliers must be INTEGERS so the curve closes across the wrap. 8 and 20
 * over 900 give the same ~112 and ~45 unit wavelengths that 2 and 5 gave over
 * 220; the extra 13 breaks the two-term symmetry so the bends do not all look
 * like the same bend.
 */
export const riverCentre = (y: number) =>
  -7.4 +
  Math.sin(phase(y) * 8 + 0.3) * 2.6 +
  Math.sin(phase(y) * 20) * 0.9 +
  Math.sin(phase(y) * 13 + 2.1) * 0.7

export const riverWidth = (y: number) =>
  6.2 + Math.sin(phase(y) * 12 + 1.4) * 1.1

/** County road. Gentler than the river — roads ARE engineered, and that
 *  contrast is what sells both. */
export const roadCentre = (y: number) =>
  8.0 + Math.sin(phase(y) * 4 + 1.1) * 1.9 + Math.sin(phase(y) * 9 + 0.4) * 0.8

/** Asphalt plus shoulder, half-width. */
const ROAD_HALF = 3.4
/** Water plus both banks, half-width, at the given y. */
const riverHalf = (y: number) => riverWidth(y) / 2 + 1.2

/**
 * The x spans nothing should be built on, at a given y.
 *
 * Callers that place objects with real footprints should widen these by their
 * own half-width rather than testing a centre point — a barn whose centre
 * clears the bank can still have half of it in the water.
 */
export function corridors(y: number): Array<[number, number]> {
  const rc = riverCentre(y)
  const rh = riverHalf(y)
  const dc = roadCentre(y)
  return [
    [rc - rh, rc + rh],
    [dc - ROAD_HALF, dc + ROAD_HALF],
  ]
}

/**
 * Worst-case corridor extents across the WHOLE wrap.
 *
 * Layout is authored in cycle-space (0..900) while the terrain repeats every
 * 220, so a prop at a fixed x meets a different part of the meander on every
 * pass. Anything static therefore has to clear the river's full sweep, not the
 * river's position at one y.
 */
export function worstCaseCorridors(): Array<[number, number]> {
  let rMin = Infinity
  let rMax = -Infinity
  let dMin = Infinity
  let dMax = -Infinity
  for (let y = 0; y < TERRAIN_WRAP; y += 0.5) {
    const rc = riverCentre(y)
    const rh = riverHalf(y)
    rMin = Math.min(rMin, rc - rh)
    rMax = Math.max(rMax, rc + rh)
    const dc = roadCentre(y)
    dMin = Math.min(dMin, dc - ROAD_HALF)
    dMax = Math.max(dMax, dc + ROAD_HALF)
  }
  return [
    [rMin, rMax],
    [dMin, dMax],
  ]
}
